import { BoardRowType } from "@common-models/task";
import { BoardRowElement } from "@common-components/tasks/helpers/elements-factory/elementsFactory";
import { selectElementsList } from "@common-reducers/TaskSelectors";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { isExtensionContextOrWhatsappView } from "@common-services/detect-context";
import { memo } from "react";
import { Virtuoso } from "react-virtuoso";
import NewTaskRow from "@common-components/tasks/new-task-row/new-task-row";
import NewTaskGroupButton from "@common-components/tasks-action-bar/add-task-group-button/new-task-group-button";
import MobileGroupHeader from "./MobileGroupHeader";
import MobileTaskCard from "./MobileTaskCard";
import classes from "./mobile-board.module.scss";

type Props = {
  isDisplayNewGroupButton?: boolean;
};

/**
 * Mobile (phone) rendering of the tasks board.
 *
 * Consumes the SAME flat `elementList` as the desktop table (via
 * `selectElementsList`) so groups, tasks, subtasks and add-item rows all appear
 * in order, but renders each task as a stacked Monday.com-style CARD instead of
 * a wide horizontal table row.
 *
 * Drag-to-reorder is intentionally NOT wired here (react-beautiful-dnd is a poor
 * touch experience); the desktop table keeps DnD.
 */
function MobileBoardView({ isDisplayNewGroupButton = true }: Props) {
  const elementList = useAppSelector(selectElementsList);
  const direction = useAppSelector((state) => state.UserReducer.direction);

  const renderItem = (_index: number, item: BoardRowElement) => {
    switch (item.type) {
      case BoardRowType.groupHeader:
        return (
          <MobileGroupHeader
            groupId={item.groupId ?? ""}
            tasksCount={item.tasksCount}
          />
        );

      case BoardRowType.boardHeader:
        return (
          <MobileGroupHeader
            groupId={item.boardId ?? ""}
            tasksCount={item.tasksCount}
          />
        );

      case BoardRowType.task:
        return (
          <MobileTaskCard taskId={item.taskId ?? ""} colorAccent={item.colorAccent} />
        );

      case BoardRowType.subTask:
        return (
          <MobileTaskCard
            taskId={item.taskId ?? ""}
            colorAccent={item.colorAccent}
            isSubTask
          />
        );

      case BoardRowType.newTask:
        return (
          <div className={classes.addRow}>
            <NewTaskRow
              groupId={item.groupId ?? ""}
              parentTaskId={item.parentTaskId}
              colorAccent={item.colorAccent}
              isLastTaskInSubTasksGroup={item.isLastTaskInSubTasksGroup}
            />
          </div>
        );

      case BoardRowType.newGroup:
        return isDisplayNewGroupButton ? (
          <div className={classes.newGroup}>
            <NewTaskGroupButton position="bottom" />
          </div>
        ) : null;

      case BoardRowType.spacer:
        return <div className={classes.spacer} />;

      default:
        return null;
    }
  };

  return (
    <div dir={direction} className={classes.wrapper}>
      <Virtuoso
        overscan={400}
        data={elementList}
        computeItemKey={(index, item) => item.id || `${item.type}-${index}`}
        itemContent={renderItem}
        className={classes.virtuoso}
        style={{
          height: isExtensionContextOrWhatsappView()
            ? "calc(100vh - 275px)"
            : "calc(100vh - 201px)",
        }}
      />
    </div>
  );
}

export default memo(MobileBoardView);
